import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../../App.css';
import './SignIn.css';
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../../services/firebase';
import zxcvbn from 'zxcvbn';
import googleLogo from '../../assets/google-logo.svg';
import LocationSelector from '../../components/common/Forms/LocationSelector';
import { authAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

auth.useDeviceLanguage();

function SignIn() {
  const { currentUser, login, register, resetPassword } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    institution: '',
    city: '',
    country: '',
    state: '',
    resetEmail: '',
    newPassword: ''
  });

  const [passwordStrength, setPasswordStrength] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [message, setMessage] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [lastResetAttempt, setLastResetAttempt] = useState(0);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const RESET_TIMEOUT = 60000;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Only redirect if user is authenticated and email is verified
    if (currentUser && currentUser.emailVerified) {
      navigate('/profile');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    const needsVerification = searchParams.get('needsVerification');
    if (needsVerification === 'true') {
      setMessage('A verification email has been sent to your email address. Please check your inbox and click the verification link to activate your account.');
    }
  }, [searchParams]);

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };


  const handlePasswordChange = (e) => {
    const value = e.target.value;
    updateFormData('password', value);
    const score = zxcvbn(value).score;
    const levels = ['Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    setPasswordStrength(levels[score]);
  };

  const handleSignUp = async () => {
    if (formData.password !== formData.confirmPassword) {
      setMessage('Passwords do not match.');
      return;
    }
  
    try {
      // 1. Create Firebase Auth user
      await register(formData.email, formData.password);
      const user = auth.currentUser;

      // 2. Send email verification first
      await sendEmailVerification(user);
  
      // 3. Get ID token
      const idToken = await user.getIdToken();
  
      // 4. Register profile in backend
      try {
        await authAPI.register({
          idToken,
          displayName: `${formData.firstName} ${formData.lastName}`,
          firstName: formData.firstName,
          lastName: formData.lastName,
          dateOfBirth: formData.dateOfBirth,
          institution: formData.institution,
          countryCode: formData.country,
          stateCode: formData.state,
          city: formData.city
        });
      } catch (profileError) {
        await user.delete(); // rollback auth creation if profile creation fails
        throw new Error('Failed to create user profile. Please try again.');
      }
  
      // 5. Sign out and show message
      await signOut(auth);
      setMessage(`A verification email has been sent to ${formData.email}. Please check your inbox.`);
      setIsSignUp(false);
  
    } catch (error) {
      setMessage(error.message || 'Failed to create account');
    }
  };  
  
  const handleSignIn = async (e) => {
    e.preventDefault();
    try {
      await login(formData.email, formData.password);
      // AuthContext or routing handles the rest
    } catch (error) {
      setMessage(error.message || 'Failed to sign in');
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
  
      const token = await user.getIdToken();
  
      // Parse name into first and last
      const names = user.displayName ? user.displayName.split(' ') : ['', ''];
      const firstName = names[0] || '';
      const lastName = names.slice(1).join(' ') || '';
  
      // Call backend register with ID token
      await authAPI.register({
        idToken: token,
        displayName: user.displayName,
        firstName,
        lastName,
        photoURL: user.photoURL,
        dateOfBirth: null,
        institution: null,
        countryCode: null,
        stateCode: null,
        city: null
      });
  
      setMessage('Signed in with Google successfully! Redirecting...');
      navigate('/profile');
  
    } catch (err) {
  
      let errorMessage = 'Failed to sign in with Google. Please try again.';
      if (err.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in popup was closed before completion.';
      } else if (err.code === 'auth/popup-blocked') {
        errorMessage = 'Sign-in popup was blocked by your browser.';
      } else if (err.code === 'auth/cancelled-popup-request') {
        errorMessage = 'Multiple popup requests detected.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your connection.';
      } else if (err.message) {
        errorMessage = err.message;
      }
  
      setMessage(errorMessage);
    }
  };
  
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    const now = Date.now();
    if (now - lastResetAttempt < RESET_TIMEOUT) {
      const remainingTime = Math.ceil((RESET_TIMEOUT - (now - lastResetAttempt)) / 1000);
      setMessage(`Please wait ${remainingTime} seconds before requesting again.`);
      return;
    }
    
    if (!formData.resetEmail) {
      setMessage('Please provide your email.');
      return;
    }
    
    try {
      await resetPassword(formData.resetEmail);
      
      setLastResetAttempt(now);
      setMessage('Password reset link has been sent to your email. Please check your inbox.');
      setShowResetForm(false);
    } catch (err) {
      setMessage(err.message || 'Failed to send password reset email');
    }
  };

  return (
    <div className="sign-in-container">
      {showResetForm ? (
        <form className="sign-in-form" onSubmit={handleResetPassword}>
          <h2>Reset Password</h2>
          {message && <p className="feedback-message">{message}</p>}
          <div className="form-group">
            <label>Email</label>
            <input 
              type="email" 
              value={formData.resetEmail} 
              onChange={(e) => updateFormData('resetEmail', e.target.value)} 
              required 
              placeholder="Enter your email address"
            />
          </div>
          <button 
            type="submit" 
            className="submit-button"
          >
            Send Reset Link
          </button>
          <p className="back-to-login">
            <span onClick={() => { setShowResetForm(false); setMessage(''); }}>
              Back to Login
            </span>
          </p>
        </form>
      ) : (
        <form className="sign-in-form" onSubmit={(e) => { e.preventDefault(); isSignUp ? handleSignUp() : handleSignIn(e); }}>
          <h2>{isSignUp ? 'Sign Up' : 'Log In'}</h2>
          {message && <p className="feedback-message">{message}</p>}

          <div className="form-group">
            <label>Email</label>
            <input type="email" value={formData.email} onChange={(e) => updateFormData('email', e.target.value)} required />
          </div>

          <div className="form-group">
            <label>Password</label>
            <div className="signin-password-input-container">
              <input 
                type={showPassword ? "text" : "password"} 
                value={formData.password} 
                onChange={handlePasswordChange} 
                required 
              />
              <span 
                className="signin-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </span>
            </div>
            {isSignUp && formData.password && (
              <p className="password-strength">Strength: {passwordStrength}</p>
            )}
          </div>

          {isSignUp && (
            <>
              <div className="form-group">
                <label>Confirm Password</label>
                <div className="signin-password-input-container">
                  <input 
                    type={showConfirmPassword ? "text" : "password"} 
                    value={formData.confirmPassword} 
                    onChange={(e) => updateFormData('confirmPassword', e.target.value)} 
                    required 
                  />
                  <span 
                    className="signin-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                  </span>
                </div>
              </div>
              <div className="form-group">
                <label>First Name</label>
                <input type="text" value={formData.firstName} onChange={(e) => updateFormData('firstName', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input type="text" value={formData.lastName} onChange={(e) => updateFormData('lastName', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Date of Birth</label>
                <input type="date" value={formData.dateOfBirth} onChange={(e) => updateFormData('dateOfBirth', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Institution</label>
                <input type="text" value={formData.institution} onChange={(e) => updateFormData('institution', e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Location</label>
                <LocationSelector
                  countryCode={formData.country}
                  stateCode={formData.state}
                  city={formData.city}
                  onCountryChange={(code) => { updateFormData('country', code); updateFormData('state', ''); updateFormData('city', ''); }}
                  onStateChange={(code) => { updateFormData('state', code); updateFormData('city', ''); }}
                  onCityChange={(city) => updateFormData('city', city)}
                  required
                />
              </div>
            </>
          )}

          {!isSignUp && (
            <div className="forgot-password">
              <span onClick={() => { setShowResetForm(true); setMessage(''); }}>Forgot Password?</span>
            </div>
          )}

          <button type="submit" className="log-in-button">
            {isSignUp ? 'Sign Up' : 'Log In'}
          </button>

          <div className="separator"><span>or continue with</span></div>
          <div className="social-sign-in">
            <button type="button" className="social-button google" onClick={handleGoogleSignIn}>
              <img src={googleLogo} alt="Google" className="social-icon" /> Google
            </button>
          </div>

          <div className="toggle-form">
            {isSignUp ? (
              <p>Already have an account? <span onClick={() => setIsSignUp(false)}>Log In</span></p>
            ) : (
              <p>Don't have an account? <span onClick={() => setIsSignUp(true)}>Sign Up</span></p>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

export default SignIn;